import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { useLogin as libUseLogin, useLogout as libUseLogout } from "@marekl/adminer";
import { apiGet, apiPost, apiPut, ApiError } from "../lib/api";
import type { Dayjs } from "dayjs";

export type Role = "reception" | "manager" | "superadmin";

export interface User {
  id: number;
  username: string;
  display_name: string;
  role: Role;
  email?: string;
  active: boolean;
  password_locked: boolean;
  last_login_at?: Dayjs;
}

// ---- Auth hooks (delegated to lib) ----

export interface LoginCredentials {
  identifier: string;
  password: string;
  remember?: boolean;
}

/** Logs in via the lib's auth adapter (POST /api/auth/login).
 * Widened to accept the optional `remember` flag; the REST adapter forwards it verbatim. */
export function useLogin() {
  return libUseLogin<User>() as unknown as UseMutationResult<User, Error, LoginCredentials>;
}

/** Logs out via the lib's auth adapter (POST /api/auth/logout). */
export function useLogout() {
  return libUseLogout();
}

// ---- Change password (kept local — kartis backend uses current_password/new_password) ----

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export function useChangePassword() {
  return useMutation<void, ApiError, ChangePasswordPayload>({
    mutationFn: (payload) => apiPost<void>("/auth/change-password", payload),
  });
}

// ---- User management (superadmin only) ----

interface UsersListResponse {
  users: User[];
}

export function useUsers() {
  return useQuery<User[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await apiGet<UsersListResponse>("/users");
      return res.users;
    },
  });
}

export interface CreateUserPayload {
  username: string;
  display_name?: string;
  password: string;
  role: Role;
  email?: string;
  password_locked?: boolean;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, CreateUserPayload>({
    mutationFn: (payload) => apiPost<User>("/users", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UpdateUserPayload {
  display_name?: string;
  role?: Role;
  email?: string;
  active?: boolean;
  password_locked?: boolean;
}

export function useUpdateUser(id: number) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UpdateUserPayload>({
    mutationFn: (payload) => apiPut<User>(`/users/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface ResetPasswordPayload {
  new_password: string;
}

export function useResetUserPassword(id: number) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ResetPasswordPayload>({
    mutationFn: (payload) => apiPost<void>(`/users/${id}/reset-password`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}
